// admin/frequencia-detail.js — overlay de detalhe da frequência de um aluno (admin).
//
// Origem (monólito v18): linhas 6317-6342 (HTML do overlay), 1446-1672 (CSS),
//   8433-8646 (função openStudentDetail v2).
//
// Restauração v19.0.1 — ver docs/removidos-sem-autorizacao-v19.md item #1.
//
// Diferenças vs v18:
//   - Recebe dados já carregados (freqMap, programacao) em vez de ler localStorage.
//   - Não precisa de busca fuzzy de aluno: o caller (frequencia-grid.js) já
//     passa o aluno normalizado com cpf/senha.
//   - Overlay é montado via DOM no primeiro open() e reutilizado.
//
// API pública:
//   open(aluno, freqData, programacao) — abre o overlay com os dados do aluno
//   close()                            — fecha o overlay

// ── Constantes ────────────────────────────────────────────────────────────────

const MESES_ABREV = ['', 'JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN',
                     'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const TURNO_ORDEM = { 'MANHÃ': 1, 'MANHA': 1, 'TARDE': 2, 'NOITE': 3, 'UNICO': 9 };

// Normaliza nome (mesmo padrão do v18 normalizarNome) — usado como chave
// no objeto de frequencia retornado pela API (que agrupa por slug do nome).
function nomeKey(nome) {
  return String(nome || '').trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Foto do professor: prioriza URL da planilha (foto_url) → arquivo local → fallback.
import { fotoProfessor, inicaisProfessor } from '../data/professores-fotos.js';

// WhatsApp: link para customer success iniciar conversa com o aluno.
import { waLink, WA_ICON_SVG } from './wa-link.js';

// ── Cache do elemento overlay (singleton) ─────────────────────────────────────

let _overlay = null;

// ── Construção do overlay (DOM + estilos inline) ──────────────────────────────

function _buildOverlay() {
  const div = document.createElement('div');
  div.id = 'freqDetailOverlay';
  div.setAttribute('role', 'dialog');
  div.setAttribute('aria-modal', 'true');
  div.setAttribute('aria-labelledby', 'fdName');
  div.style.cssText = `
    position:fixed;inset:0;background:rgba(10,10,10,0.55);z-index:350;
    display:flex;align-items:flex-end;justify-content:center;
    opacity:0;pointer-events:none;transition:opacity 0.3s ease;
    backdrop-filter:blur(4px);font-family:'Montserrat',sans-serif;
  `;

  div.innerHTML = `
    <div id="freqDetailSheet" style="
      background:#F5F5F5;border-radius:28px 28px 0 0;width:100%;max-width:600px;
      max-height:92dvh;overflow-y:auto;
      transform:translateY(100%);transition:transform 0.36s cubic-bezier(0.32,0.72,0,1);
      padding-bottom:calc(env(safe-area-inset-bottom,0px) + 24px);
    ">
      <div style="width:40px;height:4px;background:#E0E0E0;border-radius:100px;margin:14px auto 0"></div>
      <div style="background:linear-gradient(90deg,#6A001F,#B3002D);padding:20px 22px 24px;margin-top:8px;position:relative;overflow:hidden">
        <div style="position:absolute;bottom:-30px;right:-30px;width:120px;height:120px;border-radius:50%;background:rgba(255,255,255,0.07)"></div>
        <button id="freqDetailClose" aria-label="Fechar" style="
          position:absolute;top:14px;right:16px;width:30px;height:30px;border-radius:50%;
          border:none;background:rgba(255,255,255,0.2);color:white;font-size:14px;cursor:pointer;
          display:flex;align-items:center;justify-content:center;z-index:1;
        ">✕</button>
        <a id="freqDetailWa" href="#" target="_blank" rel="noopener" aria-label="Abrir conversa no WhatsApp"
          style="
          position:absolute;top:14px;right:56px;height:30px;padding:0 12px;border-radius:100px;
          background:#25D366;color:#fff;font-size:11px;font-weight:800;text-decoration:none;
          display:flex;align-items:center;gap:6px;cursor:pointer;z-index:1;
          box-shadow:0 2px 8px rgba(0,0,0,0.18);font-family:inherit;letter-spacing:.2px;
          transition:opacity .15s, transform .15s;
        ">${WA_ICON_SVG}<span>WhatsApp</span></a>
        <div id="fdAvatar" style="
          width:52px;height:52px;border-radius:50%;background:rgba(255,255,255,0.2);
          display:flex;align-items:center;justify-content:center;
          font-size:18px;font-weight:900;color:white;margin-bottom:12px;
          border:2px solid rgba(255,255,255,0.3);
        "></div>
        <div id="fdName" style="font-size:20px;font-weight:800;color:white;letter-spacing:-0.3px;line-height:1.2"></div>
        <div id="fdMeta" style="font-size:12px;font-weight:600;color:rgba(255,255,255,0.7);margin-top:4px"></div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:3px;margin-top:16px">
          <div style="background:rgba(255,255,255,0.12);border-radius:10px;padding:10px 8px;text-align:center">
            <div id="fdPresencas" style="font-size:22px;font-weight:900;color:white;line-height:1">0</div>
            <div style="font-size:9px;font-weight:700;color:rgba(255,255,255,0.6);text-transform:uppercase;letter-spacing:0.8px;margin-top:3px">Presenças</div>
          </div>
          <div style="background:rgba(255,255,255,0.12);border-radius:10px;padding:10px 8px;text-align:center">
            <div id="fdFaltas" style="font-size:22px;font-weight:900;color:white;line-height:1">0</div>
            <div style="font-size:9px;font-weight:700;color:rgba(255,255,255,0.6);text-transform:uppercase;letter-spacing:0.8px;margin-top:3px">Faltas</div>
          </div>
          <div style="background:rgba(255,255,255,0.12);border-radius:10px;padding:10px 8px;text-align:center">
            <div id="fdPct" style="font-size:22px;font-weight:900;color:white;line-height:1">—</div>
            <div style="font-size:9px;font-weight:700;color:rgba(255,255,255,0.6);text-transform:uppercase;letter-spacing:0.8px;margin-top:3px">Frequência</div>
          </div>
        </div>
      </div>
      <div id="fdBody" style="padding:20px 18px 0"></div>
    </div>
  `;

  document.body.appendChild(div);

  // Handlers de fechar
  div.querySelector('#freqDetailClose').addEventListener('click', close);

  // WhatsApp: previne clique quando desabilitado (sem telefone)
  div.querySelector('#freqDetailWa').addEventListener('click', e => {
    if (e.currentTarget.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
    }
  });

  // Click no backdrop fecha (mas não no sheet)
  div.addEventListener('click', e => {
    if (e.target === div) close();
  });

  // ESC fecha
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && div.classList.contains('open')) close();
  });

  return div;
}

// ── Cálculo do turno baseado no dia da semana (mantido do v18) ────────────────

function _calcTurnoFinal(turno, dataObj) {
  const diaSemana = dataObj.getDay(); // 0=Dom, 5=Sex, 6=Sáb
  if (diaSemana === 5) return 'NOITE';
  if (diaSemana === 6) {
    if (!turno || turno === 'UNICO' || turno === 'A definir') return 'MANHÃ';
  }
  return turno || 'UNICO';
}

// ── Busca o professor do registro consultando programação ─────────────────────

// Match primário: (data + turma + matéria) — matéria e professor são
//   colunas adjacentes na aba PROGRAMAÇÃO 2026.
// Fallback: (data + turma + turno).
// Aceita data 'dd/MM/yyyy' (registros) e 'YYYY-MM-DD' (programação).

function _isoFromBR(dataStr) {
  const m = String(dataStr || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return dataStr || '';
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

function _matchTurno(turnoFinal, aulaTurno, aulaHorario) {
  const norm = s => String(s || '').trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const t = norm(aulaTurno);
  const final = norm(turnoFinal);
  if (t && t === final) return true;
  const h = String(aulaHorario || '');
  if (final === 'noite' && h.includes('18h')) return true;
  if (final === 'manha' && h.includes('08h')) return true;
  if (final === 'tarde' && h.includes('14h')) return true;
  return false;
}

function _findAula(dataStr, turma, turnoFinal, materia, programacao) {
  if (!Array.isArray(programacao) || !programacao.length) return null;
  const dataIso = _isoFromBR(dataStr);
  const turmaNum = parseInt(turma, 10);
  const matNorm = String(materia || '').trim().toLowerCase();

  if (matNorm) {
    const exact = programacao.find(a => {
      const ad = a.data === dataStr || a.data === dataIso;
      if (!ad) return false;
      const turmas = Array.isArray(a.turmas) ? a.turmas.map(Number) : [];
      if (!turmas.includes(turmaNum)) return false;
      return String(a.materia || '').trim().toLowerCase() === matNorm;
    });
    if (exact) return exact;
  }

  return programacao.find(a => {
    const ad = a.data === dataStr || a.data === dataIso;
    if (!ad) return false;
    const turmas = Array.isArray(a.turmas) ? a.turmas.map(Number) : [];
    if (!turmas.includes(turmaNum)) return false;
    return _matchTurno(turnoFinal, a.turno, a.horario);
  }) || null;
}

// Compat: alguns callers ainda chamam _findProfessor.
function _findProfessor(dataStr, turma, turnoFinal, programacao) {
  const a = _findAula(dataStr, turma, turnoFinal, null, programacao);
  return a ? (a.professor || '') : '';
}

// ── Extrai e ordena registros de frequência ───────────────────────────────────

function _extractRegistros(dadosAluno, turma, programacao) {
  const registros = [];
  let presencas = 0, faltas = 0;

  // Caso v19: dados vêm como { presencas: {mes: n}, faltas: {mes: n}, registros: [...] }
  // Caso compatível: também aceita formato chave-valor do v18
  const lista = Array.isArray(dadosAluno?.registros) ? dadosAluno.registros : null;

  if (lista) {
    // Formato v19: lista direta de registros do endpoint da planilha
    lista.forEach(reg => {
      const dataStr = reg.data || '';
      const dp = dataStr.split('/');
      const dia = parseInt(dp[0]);
      const mes = parseInt(dp[1]);
      const ano = parseInt(dp[2]) || 2026;

      if (!dia || !mes) return;

      const dataObj = new Date(ano, mes - 1, dia);
      const turnoFinal = _calcTurnoFinal(reg.modulo || reg.turno, dataObj);

      if (reg.status === 'presente') presencas++;
      else if (reg.status === 'falta') faltas++;

      const aula = _findAula(dataStr, turma, turnoFinal, reg.materia, programacao);
      registros.push({
        dia, mes, turno: turnoFinal,
        materia: reg.materia || (aula && aula.materia) || 'A definir',
        professor: (aula && aula.professor) || '',
        // foto_url será preenchida quando a planilha PROGRAMAÇÃO 2026 tiver
        // a coluna FOTO. Até lá, fica vazia e caem no mapa local.
        professorFotoUrl: (aula && aula.foto_url) || '',
        status: reg.status || 'pendente',
        data: dataStr,
      });
    });
  } else if (dadosAluno) {
    // Fallback: formato agregado { presencas: {mes:n}, faltas: {mes:n} } sem detalhe por aula
    presencas = Object.values(dadosAluno.presencas || {}).reduce((a, b) => a + (b || 0), 0);
    faltas    = Object.values(dadosAluno.faltas    || {}).reduce((a, b) => a + (b || 0), 0);
  }

  // Ordena por data e depois turno
  registros.sort((a, b) => {
    const dA = new Date(2026, a.mes - 1, a.dia);
    const dB = new Date(2026, b.mes - 1, b.dia);
    if (dA.getTime() !== dB.getTime()) return dA - dB;
    return (TURNO_ORDEM[a.turno] || 9) - (TURNO_ORDEM[b.turno] || 9);
  });

  return { registros, presencas, faltas };
}

// ── Render do corpo (lista de aulas) ──────────────────────────────────────────

function _renderBody(bodyEl, registros) {
  if (!registros.length) {
    bodyEl.innerHTML = `
      <div style="background:#fff;border-radius:12px;padding:32px;text-align:center;color:#9B9B9B;font-size:13px">
        Nenhum registro de aula para este aluno ainda.
      </div>`;
    return;
  }

  const fragments = registros.map(r => {
    const emoji = r.status === 'presente' ? '✅' : r.status === 'falta' ? '❌' : '📅';
    const cor   = r.status === 'presente' ? '#34c759' : r.status === 'falta' ? '#ff3b30' : '#999';
    const classe = r.status === 'presente' ? 'presente' : r.status === 'falta' ? 'falta' : 'futuro';
    const bgStatus = classe === 'presente' ? '#e8f5e9' : classe === 'falta' ? '#fce4ec' : '#F5F5F5';

    const iniciais = r.professor ? inicaisProfessor(r.professor) : '?';
    const fotoUrl  = r.professor ? fotoProfessor(r.professor, r.professorFotoUrl) : null;
    const altProf  = (r.professor || 'Professor a definir').replace(/"/g, '&quot;');

    // Avatar do professor: img se houver foto local/remota, senão círculo c/ iniciais.
    // onerror cai pro círculo de iniciais se a imagem falhar.
    const avatarHtml = fotoUrl
      ? `<div style="
           width:36px;height:36px;border-radius:50%;overflow:hidden;flex-shrink:0;
           background:linear-gradient(135deg,#6A001F,#B3002D);
           display:flex;align-items:center;justify-content:center;
         " title="${altProf}">
           <img src="${fotoUrl}" alt="${altProf}" loading="lazy"
             style="width:100%;height:100%;object-fit:cover;display:block"
             onerror="this.outerHTML='<span style=&quot;color:#fff;font-weight:700;font-size:12px&quot;>${iniciais}</span>'">
         </div>`
      : `<div style="
           width:36px;height:36px;border-radius:50%;
           background:linear-gradient(135deg,#6A001F,#B3002D);
           display:flex;align-items:center;justify-content:center;
           font-size:12px;font-weight:700;color:#fff;flex-shrink:0;
         " title="${altProf}">${iniciais}</div>`;

    return `
      <div style="
        background:white;border-radius:12px;padding:12px 14px;margin-bottom:8px;
        display:flex;align-items:center;gap:12px;box-shadow:0 1px 4px rgba(0,0,0,0.05);
      ">
        <div style="min-width:40px;text-align:center">
          <div style="font-size:18px;font-weight:900;color:#6A001F;line-height:1">${r.dia}</div>
          <div style="font-size:9px;font-weight:700;color:#9B9B9B;text-transform:uppercase;margin-top:2px">${MESES_ABREV[r.mes] || ''}</div>
        </div>
        <div style="width:1px;height:32px;background:#F0F0F0;flex-shrink:0"></div>
        ${avatarHtml}
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:700;color:#0A0A0A">${r.materia}</div>
          ${r.professor ? `<div style="font-size:11px;font-weight:600;color:#6A001F;margin-top:1px">${r.professor}</div>` : ''}
          <div style="font-size:11px;font-weight:600;color:#B3002D;margin-top:1px">${r.turno}</div>
          <div style="font-size:10px;font-weight:700;color:${cor};margin-top:4px">${r.status.toUpperCase()}</div>
        </div>
        <div style="
          width:30px;height:30px;border-radius:50%;background:${bgStatus};
          display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;
        ">${emoji}</div>
      </div>`;
  });

  bodyEl.innerHTML = fragments.join('');
}

// ── API pública ───────────────────────────────────────────────────────────────

/**
 * Abre o overlay com os detalhes de frequência de um aluno.
 * @param {object} aluno - { nome, turma, cpf, senha }
 * @param {object} freqData - mapa de frequência (key = cpf ou senha do aluno)
 * @param {Array}  programacao - lista de aulas da planilha (opcional, para professor)
 */
export function open(aluno, freqData, programacao) {
  if (!_overlay) _overlay = _buildOverlay();

  // A API agrupa frequencia por nome normalizado (slug minúsculo sem acentos).
  // Usamos `_nomeRaw` (nome original da planilha) para garantir o match;
  // o `aluno.nome` já pode estar em Title Case e não bater na chave.
  const dadosAluno = (freqData && (
    freqData[nomeKey(aluno._nomeRaw || aluno.nome)] ||
    freqData[aluno.cpf] ||
    freqData[aluno.senha]
  )) || null;

  const { registros, presencas, faltas } = _extractRegistros(dadosAluno, aluno.turma, programacao);
  const total = presencas + faltas;
  const pct = total > 0 ? Math.round((presencas / total) * 100) + '%' : '—';

  // Header
  const initials = aluno.nome.split(' ').slice(0, 2).map(n => n[0]).join('');
  _overlay.querySelector('#fdAvatar').textContent    = initials;
  _overlay.querySelector('#fdName').textContent      = aluno.nome;
  _overlay.querySelector('#fdMeta').textContent      = `Turma ${aluno.turma} · MBO Pós · 2026`;
  _overlay.querySelector('#fdPresencas').textContent = presencas;
  _overlay.querySelector('#fdFaltas').textContent    = faltas;
  _overlay.querySelector('#fdPct').textContent       = pct;

  // Botão WhatsApp — habilita/desabilita conforme disponibilidade do telefone.
  // Monta mensagem listando todas as faltas (matéria + data) para a CS (Gabi).
  const wa = _overlay.querySelector('#freqDetailWa');
  const faltasAluno = registros
    .filter(r => r.status === 'falta')
    .map(r => ({ materia: r.materia, data: r.data }));
  const link = waLink(aluno.telefone, aluno.nome, faltasAluno);
  if (link) {
    wa.href = link;
    wa.removeAttribute('aria-disabled');
    wa.title = 'Abrir conversa no WhatsApp';
    wa.style.opacity = '1';
    wa.style.cursor = 'pointer';
    wa.style.pointerEvents = 'auto';
  } else {
    wa.href = '#';
    wa.setAttribute('aria-disabled', 'true');
    wa.title = 'Sem telefone cadastrado';
    wa.style.opacity = '0.5';
    wa.style.cursor = 'not-allowed';
  }

  // Body
  _renderBody(_overlay.querySelector('#fdBody'), registros);

  // Abre
  _overlay.style.opacity = '1';
  _overlay.style.pointerEvents = 'all';
  _overlay.classList.add('open');
  const sheet = _overlay.querySelector('#freqDetailSheet');
  sheet.style.transform = 'translateY(0)';
  document.body.style.overflow = 'hidden';
}

export function close() {
  if (!_overlay) return;
  _overlay.style.opacity = '0';
  _overlay.style.pointerEvents = 'none';
  _overlay.classList.remove('open');
  const sheet = _overlay.querySelector('#freqDetailSheet');
  sheet.style.transform = 'translateY(100%)';
  document.body.style.overflow = '';
}
